/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ClientGroupExtendedDetail } from '@DigitaleDoerfer/digitale-doerfer-api';
import DeleteGroupActionView from './DeleteGroupAction.view';
import {
	showSnackbar,
	SnackbarAction,
	SnackbarType
} from '../../../../shared/views/SnackbarNotification/store/SnackbarNotification.actions';
import { ActionMenuItemProps } from '../../../../shared/views/ActionMenu/ActionMenu';
import { RoleKey, RoleManagementPermission } from '../../../../shared/services/Role.service';
import { roleService } from '../../../../ServiceFactory';
import { PlatformAdminUIState, PlatformAdminUIThunkType } from '../../../../store/store';
import { UserActionTypes } from '../../../users/store/UserActionTypes';
import { groupDeleteByAdmin } from '../../store/Group.actions';

interface Props extends ActionMenuItemProps {
	group: ClientGroupExtendedDetail | null;
	roleManagementPermission: RoleManagementPermission;
	showSnackbar: (snackbarAction: SnackbarAction) => void;
	deleteGroup: (groupId: string, forceDeleteNonEmptyGroups: boolean) => void;
}

class DeleteGroupActionContainer extends Component<Props> {
	constructor(props: Props) {
		super(props);

		this.handleDelete = this.handleDelete.bind(this);
	}

	handleDelete(groupId: string, forceDeleteNonEmptyGroups: boolean): void {
		const { showSnackbar, deleteGroup } = this.props;
		if (groupId) {
			deleteGroup(groupId, forceDeleteNonEmptyGroups);
		} else {
			showSnackbar({
				type: SnackbarType.SNACKBAR_ERROR,
				message: 'Die Gruppe hat keine ID.'
			});
		}
	}

	render(): JSX.Element | null {
		const { renderMenuItem, group, roleManagementPermission } = this.props;
		if (!group) {
			return null;
		}

		if (
			!roleService().hasClientRoleAssignmentSomeValidRole(roleManagementPermission.roles, [RoleKey.GLOBAL_GROUP_ADMIN])
		) {
			return null;
		}

		return <DeleteGroupActionView renderMenuItem={renderMenuItem} group={group} handleDelete={this.handleDelete} />;
	}
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: PlatformAdminUIState) => {
	return {
		group: state.group.detail.group,
		roleManagementPermission: state.global.roleManagementPermission
	};
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: PlatformAdminUIThunkType<UserActionTypes>) => {
	return {
		showSnackbar: (snackbarAction: SnackbarAction): Promise<void> => dispatch(showSnackbar(snackbarAction)),
		deleteGroup: (groupId: string, forceDeleteNonEmptyGroups: boolean): Promise<void> =>
			dispatch(groupDeleteByAdmin(forceDeleteNonEmptyGroups, groupId))
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(DeleteGroupActionContainer);
